// PRE RMA — Semaines épidémiologiques (rapports hebdomadaires RSH / MNT, export DHIS2).
//
// RÈGLE : semaine ISO 8601 = du LUNDI au DIMANCHE ; la semaine 1 est celle qui contient le 4 janvier.
// Justification : l'export DHIS2 fourni (simr.snis-sante.net, v2.40.8.2) n'a pas modifié le paramètre
// ANALYTICS_WEEK_START (valeur nulle = comportement par défaut de DHIS2 = semaine ISO, lundi). Le « S: 41 » des
// modèles RSH et MNT/MTN correspond donc à la semaine ISO n° 41 (en 2026 : lundi 5 au dimanche 11 octobre).
// Si le programme décide d'une autre convention, SEUL ce fichier doit changer.
//
// Toutes les dates sont des chaînes « AAAA-MM-JJ » (jour calendaire, sans fuseau) : aucune dérive due à l'heure d'été
// ou au fuseau (Indian/Antananarivo = UTC+3). Les calculs passent par Date.UTC uniquement pour l'arithmétique.

export interface SemaineISO {
  annee: number;      // année ISO (peut différer de l'année civile autour du 1er janvier)
  semaine: number;    // 1..52/53
  debut: string;      // lundi, AAAA-MM-JJ
  fin: string;        // dimanche, AAAA-MM-JJ
  jours: string[];    // les 7 dates
  /** Identifiant de période DHIS2 hebdomadaire, ex. « 2026W41 ». */
  periodeDhis2: string;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_JOUR = 86_400_000;

/** true si la chaîne est un vrai jour du calendrier (refuse 2026-02-30, 2026-13-01…). */
export function dateValide(s: unknown): s is string {
  if (typeof s !== 'string') return false;
  const m = DATE_RE.exec(s);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = new Date(Date.UTC(y, mo - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d;
}

const enUtc = (s: string): number => { const m = DATE_RE.exec(s)!; return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])); };
const enChaine = (t: number): string => new Date(t).toISOString().slice(0, 10);

export function ajouterJours(s: string, n: number): string {
  if (!dateValide(s)) throw new Error(`Date invalide : ${s}`);
  return enChaine(enUtc(s) + n * MS_JOUR);
}

/** Jour de la semaine ISO : 1 = lundi … 7 = dimanche. */
export function jourSemaineISO(s: string): number {
  if (!dateValide(s)) throw new Error(`Date invalide : ${s}`);
  const j = new Date(enUtc(s)).getUTCDay(); // 0 = dimanche
  return j === 0 ? 7 : j;
}

/** Lundi de la semaine 1 de l'année ISO donnée (= lundi de la semaine contenant le 4 janvier). */
function lundiSemaine1(annee: number): number {
  const quatreJanv = Date.UTC(annee, 0, 4);
  const j = new Date(quatreJanv).getUTCDay() || 7;
  return quatreJanv - (j - 1) * MS_JOUR;
}

/** 52 ou 53 : une année ISO a 53 semaines si son 1er janvier est un jeudi (ou un mercredi en année bissextile). */
export function nbSemainesISO(annee: number): 52 | 53 {
  const debut = lundiSemaine1(annee), suivant = lundiSemaine1(annee + 1);
  return Math.round((suivant - debut) / (7 * MS_JOUR)) as 52 | 53;
}

export function semaineISO(annee: number, semaine: number): SemaineISO {
  if (!Number.isInteger(annee) || annee < 2000 || annee > 2100) throw new Error(`Année invalide : ${annee}`);
  const max = nbSemainesISO(annee);
  if (!Number.isInteger(semaine) || semaine < 1 || semaine > max) throw new Error(`Semaine invalide : ${semaine} (l'année ${annee} compte ${max} semaines)`);
  const lundi = lundiSemaine1(annee) + (semaine - 1) * 7 * MS_JOUR;
  const jours = Array.from({ length: 7 }, (_, i) => enChaine(lundi + i * MS_JOUR));
  return { annee, semaine, debut: jours[0], fin: jours[6], jours, periodeDhis2: `${annee}W${semaine}` };
}

/** Semaine ISO (année ISO + numéro) d'une date. */
export function semaineDeDate(s: string): { annee: number; semaine: number } {
  if (!dateValide(s)) throw new Error(`Date invalide : ${s}`);
  const t = enUtc(s);
  const jeudi = t + (4 - jourSemaineISO(s)) * MS_JOUR; // le jeudi de la même semaine détermine l'année ISO
  const anneeISO = new Date(jeudi).getUTCFullYear();
  const semaine = Math.floor((lundiSemaineDe(t) - lundiSemaine1(anneeISO)) / (7 * MS_JOUR)) + 1;
  return { annee: anneeISO, semaine };
}
function lundiSemaineDe(t: number): number { const j = new Date(t).getUTCDay() || 7; return t - (j - 1) * MS_JOUR; }

export const dansSemaine = (date: string | null | undefined, s: Pick<SemaineISO, 'debut' | 'fin'>): boolean =>
  typeof date === 'string' && dateValide(date) && date >= s.debut && date <= s.fin;

/** Libellé lisible : « S41 2026 — du lundi 05/10/2026 au dimanche 11/10/2026 ». */
export function libelleSemaine(s: SemaineISO): string {
  const fr = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
  return `S${s.semaine} ${s.annee} — du lundi ${fr(s.debut)} au dimanche ${fr(s.fin)}`;
}

/** Période DHIS2 journalière : « 20261012 » (le jeu de données RSJ de l'export fourni est de type Daily). */
export const periodeDhis2Jour = (date: string): string => {
  if (!dateValide(date)) throw new Error(`Date invalide : ${date}`);
  return date.replace(/-/g, '');
};

/** Mois (1-12) et année civile auxquels appartient une date. */
export const moisDeDate = (date: string): { annee: number; mois: number } => {
  if (!dateValide(date)) throw new Error(`Date invalide : ${date}`);
  return { annee: Number(date.slice(0, 4)), mois: Number(date.slice(5, 7)) };
};

/**
 * Une date de consultation doit appartenir au MOIS de la période (periodes.annee/mois) dans laquelle elle est saisie :
 * c'est ce qui garantit que le RMA mensuel et les rapports hebdomadaires comptent les mêmes lignes.
 * Retourne un message d'erreur, ou null si tout est correct. `aujourdhui` est injecté pour rendre la règle testable.
 */
export function controlerDateSaisie(date: unknown, periode: { annee: number; mois: number }, aujourdhui: string): string | null {
  if (!dateValide(date)) return 'Date invalide (format AAAA-MM-JJ attendu).';
  if (date > ajouterJours(aujourdhui, 1)) return 'La date est dans le futur.'; // 1 jour de tolérance (fuseaux / saisie hors connexion)
  const m = moisDeDate(date);
  if (m.annee !== periode.annee || m.mois !== periode.mois) {
    return `La date ${date} n'appartient pas à la période ${String(periode.mois).padStart(2, '0')}/${periode.annee}.`;
  }
  return null;
}
